import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../atoms/Input';

/**
 * SearchBar Component - Molecular Level
 * 
 * Search input with keyboard shortcuts and suggestions
 * 
 * Features:
 * - Keyboard shortcut (Cmd/Ctrl + K)
 * - Real-time search
 * - Search suggestions (optional)
 * - Clear button
 * - Loading state
 */

export interface SearchBarProps {
  /** Placeholder text */
  placeholder?: string;
  /** Search handler */
  onSearch?: (query: string) => void;
  /** Change handler (fires on every keystroke) */
  onChange?: (query: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
  /** Show keyboard shortcut hint */
  showShortcut?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search here',
  onSearch,
  onChange,
  isLoading = false,
  className = '',
  showShortcut = true,
}) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    onChange?.(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(query);
  };

  const handleClear = () => {
    setQuery('');
    onChange?.('');
    inputRef.current?.focus();
  };

  const searchIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );

  const rightContent = query ? (
    <button
      type="button"
      onClick={handleClear}
      className="text-gray-400 hover:text-gray-600 transition-colors"
      aria-label="Clear search"
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z"
          clipRule="evenodd"
        />
      </svg>
    </button>
  ) : showShortcut && !isFocused ? (
    <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded">
      ⌘K
    </kbd>
  ) : null;

  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        type="search"
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        leftIcon={isLoading ? (
          <svg
            className="animate-spin h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          searchIcon
        )}
        rightIcon={rightContent}
        className="w-full"
        aria-label="Search"
      />
    </form>
  );
};

export default SearchBar;

